<script setup lang="ts">
// The FOF home's chart (Paolo 2026-11-03 18:1x, "Two-Chart FOF Row"): one path, split at the bars inside the window —
// the previous exchange close (grey, dashed, "ON ›" to its right), the 08:40 decision (in the ON section's colour) and
// the 12:00 strike (blue) — each section coloured by its own drift (sectionSpark.ts). On K_t the strike bar carries the
// revaluation step (green when the NAV came in below the synth, red above; Paolo's convention) and the section after it
// is the drift from the official NAV, whose level is a dashed line — in the zoom drawing only (Paolo 2026-11-03 19:xx:
// no horizontal dotted line in the row cells). `size="mini"` is the row cell (no text); `size="zoom"` is the full
// drawing with a grid, value labels, UK time labels, the bar labels, the base level and each section's drift %.
import { computed } from 'vue'
import { sections, barColours, stepColour, strikeStepIndex, type SectionBars } from './sectionSpark'

const props = withDefaults(defineProps<{
  values: number[]
  size?: 'mini' | 'zoom'
  upIsGood?: boolean
  closeAt?: number | null
  decisionAt?: number | null
  markAt?: number | null
  baseValue?: number | null
  baseLabel?: string
  markLabel?: string
  decisionLabel?: string
  stepFrom?: number | null
  stepTo?: number | null
  startUtc?: string | null
  endUtc?: string | null
  unit?: string
  dp?: number
  decisionColour?: string | null   // Paolo 2026-11-03 19:3x: the K_t chart's decision bar borrows the futures chart's colour
}>(), { size: 'mini', upIsGood: true, closeAt: null, decisionAt: null, markAt: null, baseValue: null, baseLabel: 'base',
  markLabel: 'strike', decisionLabel: '08:40', stepFrom: null, stepTo: null, startUtc: null, endUtc: null, unit: '', dp: 2, decisionColour: null })

const zoom = computed(() => props.size === 'zoom')
const W = computed(() => zoom.value ? 600 : 160), H = computed(() => zoom.value ? 240 : 36)
const L = computed(() => zoom.value ? 52 : 1), R = computed(() => zoom.value ? 14 : 1), T = computed(() => zoom.value ? 16 : 2), B = computed(() => zoom.value ? 26 : 2)
const GRID = '#2a2a2a', MUTED = '#9e9e9e', BASE = '#e0e0e0'
const n = computed(() => props.values.length)
const bars = computed<SectionBars>(() => ({ closeAt: props.closeAt, decisionAt: props.decisionAt, markAt: props.markAt }))
const step = computed(() => strikeStepIndex(props.values, props.markAt, props.stepFrom, props.stepTo))
const secs = computed(() => sections(props.values, bars.value, props.upIsGood, step.value))
const barCol = computed(() => {
  const c = barColours(secs.value, bars.value, n.value)
  return props.decisionColour && c.decision ? { ...c, decision: props.decisionColour } : c
})
const base = computed(() => props.baseValue != null && props.baseValue > 0 ? props.baseValue : null)
const domain = computed(() => {
  const ys = props.values.concat(base.value != null ? [base.value] : [])
  let lo = Math.min(...ys), hi = Math.max(...ys)
  if (!(hi > lo)) { lo -= 1; hi += 1 }
  const pad = (hi - lo) * (zoom.value ? 0.08 : 0.12)
  return { lo: lo - pad, hi: hi + pad }
})
const plotW = computed(() => W.value - L.value - R.value)
const xAt = (f: number) => L.value + f * plotW.value
const X = (i: number) => n.value < 2 ? L.value : xAt(i / (n.value - 1))
const Y = (v: number) => T.value + (domain.value.hi - v) / (domain.value.hi - domain.value.lo) * (H.value - T.value - B.value)
const frac = (f: number | null | undefined) => f == null || !Number.isFinite(f) || f < 0 || f > 1 || n.value < 2 ? null : f
const markX = computed(() => { const f = frac(props.markAt); return f == null ? null : xAt(f) })
const closeX = computed(() => { const f = frac(props.closeAt); return f == null || f === 0 ? null : xAt(f) })
const decisionX = computed(() => { const f = frac(props.decisionAt); return f == null ? null : xAt(f) })
// the step pair sits on the strike's x; everything else on its index
const xs = (i: number) => (step.value >= 0 && (i === step.value || i === step.value + 1) && markX.value !== null) ? markX.value : X(i)
const paths = computed(() => secs.value.map(s => {
  let d = ''
  for (let i = s.from; i <= s.to; i++) {
    if (step.value >= 0 && i === step.value + 1 && s.from !== i) continue   // the step's lower end is the next section's start
    d += (d ? ' L' : 'M') + xs(i).toFixed(1) + ',' + Y(props.values[i]).toFixed(1)
  }
  const a = props.values[s.from], b = props.values[s.to]
  return { d, colour: s.colour, kind: s.kind, pct: a > 0 ? (b / a - 1) * 100 : 0, mid: (xs(s.from) + xs(s.to)) / 2, width: xs(s.to) - xs(s.from) }
}))
const stepLine = computed(() => step.value < 0 || markX.value === null ? null
  : { x: markX.value, y1: Y(props.values[step.value]), y2: Y(props.values[step.value + 1]),
      colour: stepColour(props.values[step.value], props.values[step.value + 1], props.upIsGood),
      pct: (props.values[step.value + 1] / props.values[step.value] - 1) * 100 })
const last = computed(() => n.value ? { x: xs(n.value - 1), y: Y(props.values[n.value - 1]), colour: secs.value.length ? secs.value[secs.value.length - 1].colour : MUTED } : null)
const niceStep = (range: number) => { const raw = range / 4; const p = Math.pow(10, Math.floor(Math.log10(raw))); const m = raw / p; return (m >= 5 ? 5 : m >= 2 ? 2 : 1) * p }
const grid = computed(() => {
  if (!zoom.value) return []
  const { lo, hi } = domain.value; const st = niceStep(hi - lo); const out: { y: number; label: string }[] = []
  const dp = st >= 1 ? 0 : st >= 0.1 ? 1 : 2
  for (let v = Math.ceil(lo / st) * st; v <= hi; v += st) out.push({ y: Y(v), label: v.toFixed(dp) })
  return out
})
function timeAt(f: number): string {
  if (!props.startUtc || !props.endUtc) return ''
  const a = Date.parse(props.startUtc), b = Date.parse(props.endUtc)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return ''
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(a + (b - a) * f))
}
const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`
const fmt = (v: number) => v.toLocaleString('en-GB', { minimumFractionDigits: props.dp, maximumFractionDigits: props.dp })
const sw = computed(() => zoom.value ? 2 : 1.6)
defineExpose({ xs })
</script>

<template>
  <svg class="section-spark" :class="size" :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" data-testid="section-spark" role="img">
    <template v-if="n >= 2">
      <template v-if="zoom">
        <line v-for="g in grid" :key="'g' + g.y" :x1="L" :x2="W - R" :y1="g.y" :y2="g.y" :stroke="GRID" stroke-width="1" />
        <text v-for="g in grid" :key="'t' + g.y" :x="L - 6" :y="g.y + 4" text-anchor="end" font-size="11" :fill="MUTED">{{ g.label }}</text>
        <text :x="L" :y="H - 8" font-size="11" :fill="MUTED">{{ timeAt(0) }}</text>
        <text :x="W - R" :y="H - 8" text-anchor="end" font-size="11" :fill="MUTED">{{ timeAt(1) }}</text>
      </template>
      <line v-if="zoom && base !== null" data-testid="ss-base" :x1="L" :x2="W - R" :y1="Y(base)" :y2="Y(base)" :stroke="BASE" stroke-width="1" stroke-dasharray="2,4" opacity="0.7" />
      <text v-if="zoom && base !== null" :x="W - R" :y="Y(base) - 5" text-anchor="end" font-size="11" :fill="MUTED">{{ baseLabel }} {{ fmt(base) }}{{ unit }}</text>
      <line v-if="closeX !== null" data-testid="ss-close" :x1="closeX" :x2="closeX" :y1="T" :y2="H - B" :stroke="barCol.close" stroke-width="1" stroke-dasharray="3,3" />
      <text v-if="zoom && closeX !== null" :x="closeX + 4" :y="H - B - 4" font-size="11" :fill="barCol.close">ON ›</text>
      <line v-if="decisionX !== null && barCol.decision" data-testid="ss-decision" :x1="decisionX" :x2="decisionX" :y1="T" :y2="H - B" :stroke="barCol.decision" stroke-width="1.2" />
      <text v-if="zoom && decisionX !== null && barCol.decision" :x="decisionX + 4" :y="T + 10" font-size="11" :fill="barCol.decision">{{ decisionLabel }}</text>
      <line v-if="markX !== null" data-testid="ss-strike" :x1="markX" :x2="markX" :y1="T" :y2="H - B" :stroke="barCol.strike" :stroke-width="zoom ? 2 : 1.4" />
      <text v-if="zoom && markX !== null" :x="markX + 4" :y="T + 22" font-size="11" :fill="barCol.strike">{{ markLabel }}</text>
      <path v-for="(p, i) in paths" :key="i" :d="p.d" fill="none" :stroke="p.colour" :stroke-width="sw" stroke-linejoin="round" stroke-linecap="round"
            data-testid="ss-section" :data-kind="p.kind" />
      <template v-if="zoom">
        <text v-for="(p, i) in paths.filter(q => q.width > 40)" :key="'p' + i" :x="p.mid" :y="T - 4" text-anchor="middle" font-size="10" :fill="p.colour">{{ signed(p.pct) }}</text>
      </template>
      <line v-if="stepLine" data-testid="ss-step" :x1="stepLine.x" :x2="stepLine.x" :y1="stepLine.y1" :y2="stepLine.y2" :stroke="stepLine.colour" :stroke-width="zoom ? 4 : 3" stroke-linecap="round" />
      <text v-if="zoom && stepLine" :x="stepLine.x + 6" :y="(stepLine.y1 + stepLine.y2) / 2 + 4" font-size="10" :fill="stepLine.colour">reval {{ signed(stepLine.pct) }}</text>
      <circle v-if="last" :cx="last.x" :cy="last.y" :r="zoom ? 3.5 : 2" :fill="last.colour" />
    </template>
    <line v-else data-testid="section-spark-empty" x1="0" :y1="H / 2" :x2="W" :y2="H / 2" stroke="#333" stroke-width="1" stroke-dasharray="4,3" />
  </svg>
</template>

<style scoped>
.section-spark { display: block; width: 100%; font-family: 'JetBrains Mono', Consolas, monospace; }
.section-spark.mini { height: 36px; }
.section-spark.zoom { height: 230px; }
</style>
